import { supabase } from "@/infrastructure/supabase/client";
import type { Rag, RiskRow, Severity, Status } from "@/infrastructure/supabase/database.types";

export interface RiskFilters {
  status?: Status;
  rag?: Rag;
  severity?: Severity;
  ownerId?: string;
}

export const risksRepository = {
  async list(filters: RiskFilters = {}): Promise<RiskRow[]> {
    let q = supabase
      .from("risks")
      .select("*")
      .order("rag", { ascending: true })
      .order("code", { ascending: true });
    if (filters.status) q = q.eq("status", filters.status);
    if (filters.rag) q = q.eq("rag", filters.rag);
    if (filters.severity) q = q.eq("severity", filters.severity);
    if (filters.ownerId) q = q.eq("owner_id", filters.ownerId);
    const { data, error } = await q;
    if (error) throw error;
    return data ?? [];
  },

  async byId(id: string): Promise<RiskRow | null> {
    const { data, error } = await supabase.from("risks").select("*").eq("id", id).maybeSingle();
    if (error) throw error;
    return data;
  },

  async update(id: string, patch: Partial<RiskRow>): Promise<RiskRow> {
    const { data, error } = await supabase
      .from("risks")
      .update(patch)
      .eq("id", id)
      .select("*")
      .single();
    if (error) throw error;
    return data;
  },

  async create(input: Omit<RiskRow, "id" | "created_at" | "updated_at">): Promise<RiskRow> {
    const { data, error } = await supabase.from("risks").insert(input).select("*").single();
    if (error) throw error;
    return data;
  },

  subscribeToChanges(onChange: () => void) {
    const channel = supabase
      .channel("risks-changes")
      .on("postgres_changes", { event: "*", schema: "public", table: "risks" }, onChange)
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  },
};
